/**
 * @file ImportQueuePage.js
 * @description A React component that displays and manages the import queue for file processing operations with sorting, filtering, and pagination support.
 */
import { useEffect, useState, useMemo } from 'react';
import '../page-list/PageList.css';
import './ImportQueuePage.css';
import styles from './ImportQueuePage.module.scss';
import { getDateFromZulu } from '../helpers/getDateFromZulu';
import { getBadgeStatus } from '../helpers/getBadgeStatus';
import {
    getImportReport,
    getImportData,
    getOriginalFile,
    deleteImport,
} from '../../services/importService';
import { useInterval } from '../../hooks/useInterval';
import Spinner from '../ui/spinners/Spinner';
import {
    FilterModal,
    useFilter,
    renderFilterIcon,
} from '../helpers/FilterComponent';
import PaginationComponent from '../helpers/PaginationComponent';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import { logEvent } from '../../services/loggingService';
import { LogEventType } from '../../utils/data/enums';
import Toast from '../ui/toasts/Toast';
import { getText } from '../../i18n';

const SortDirections = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const getImportTypeDisplayLabel = item => {
    const importTemplateType = (item?.importTemplateType || '').trim();

    if (importTemplateType.length > 0) {
        return importTemplateType;
    }

    const explicitType = (
        item?.type ||
        item?.importType ||
        item?.category ||
        ''
    ).trim();

    if (explicitType.length > 0) {
        return explicitType;
    }

    if (item?.importTypeId === 0) {
        return getText('msg_09cb6197bf14');
    }

    return '';
};

const getDeleteSuccessMessage = count =>
    count === 1
        ? getText('msg_4eb1c7a2d901')
        : getText('msg_5fc2d8b3ea12', { count });

const ImportQueuePage = () => {
    const [imports, setImports] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [baseFilteredData, setBaseFilteredData] = useState([]);
    const [filteredData, applyFilter, clearAllFilters] =
        useFilter(baseFilteredData);
    const [selections, setSelections] = useState({});
    const [activeModal, setActiveModal] = useState(null);
    const [sortConfig, setSortConfig] = useState({
        key: 'dateTime',
        direction: SortDirections.DESCENDING,
    });
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [checkedImportIds, setCheckedImportIds] = useState([]);

    useEffect(() => {
        const searchFiltered = imports.filter(
            importItem =>
                importItem.template
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                importItem.importType
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                importItem.submittedBy
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase()),
        );
        setBaseFilteredData(searchFiltered);
    }, [searchTerm, imports]);

    useEffect(() => {
        loadImportData(true);
    }, [currentPage]);

    useEffect(() => {
        if (!initialLoadComplete) return;

        const timeoutId = setTimeout(() => {
            loadImportData();
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, sortConfig]);

    useInterval(() => {
        if (!isLoading) {
            loadImportData();
        }
    }, 10000);

    const loadImportData = async (showSpinner = false) => {
        if (!initialLoadComplete || showSpinner) setIsLoading(true);

        try {
            const sortParam = sortConfig.key + sortConfig.direction;

            const response = await getImportData(
                currentPage,
                pageSize,
                null,
                sortParam,
                searchTerm,
            );

            if (response) {
                const formattedImports = formatImportData(response.viewModels);
                setImports(formattedImports);
                setBaseFilteredData(formattedImports);
                setTotalPages(Math.ceil(response.pageTotal));
                setInitialLoadComplete(true);
            }
        } catch (error) {
            console.error('Failed to fetch import data', error);
        } finally {
            if (isLoading || showSpinner) setIsLoading(false);
        }
    };

    const formatImportData = viewModels => {
        return viewModels.map(item => {
            const resultsReport = item.reports.find(
                ({ importReportTypeId }) => importReportTypeId === 2,
            );
            const changesReport = item.reports.find(
                ({ importReportTypeId }) => importReportTypeId === 3,
            );
            const importTypeLabel = getImportTypeDisplayLabel(item);

            return {
                id: item.id,
                tenantId: item.tenantId,
                batchId: item.batchId ?? item.id,
                dateTime: getDateFromZulu(item.createdUTC),
                template: item.template || `Standard ${importTypeLabel}`,
                importType: importTypeLabel,
                status: item.status,
                test: item.isTest,
                submittedBy: item.createdBy,
                records:
                    item.totalRecords > 0
                        ? `${item.totalRecords - item.errors}/${item.totalRecords}`
                        : '',
                changesReportId: changesReport ? changesReport.id : 0,
                resultsReportId: resultsReport ? resultsReport.id : 0,
                displayFileName: item.displayFileName,
                fileType: item.fileType,
                totalRecords: item.totalRecords,
                errors: item.errors,
                canDelete: item.canDelete,
            };
        });
    };

    const openModal = column => {
        setActiveModal(column);
    };

    const closeModal = () => {
        setActiveModal(null);
    };

    const handleApply = (column, selectedOptions) => {
        setSelections(prev => ({ ...prev, [column]: selectedOptions }));
        applyFilter(column, selectedOptions);
    };

    const clearFilters = () => {
        setSelections({});
        setSearchTerm('');
        clearAllFilters();
    };

    const getOptions = column => {
        const filtersWithoutCurrent = { ...selections };
        delete filtersWithoutCurrent[column];

        const filteredData = imports.filter(importItem =>
            Object.entries(filtersWithoutCurrent).every(
                ([key, values]) =>
                    values.length === 0 || values.includes(importItem[key]),
            ),
        );

        return Array.from(new Set(filteredData.map(item => item[column])))
            .filter(Boolean)
            .sort((a, b) => {
                if (typeof a === 'string' && typeof b === 'string') {
                    return a.localeCompare(b);
                }
                return a - b;
            });
    };

    const handleSort = key => {
        setSortConfig(prevConfig => ({
            key,
            direction:
                prevConfig.key === key &&
                prevConfig.direction === SortDirections.ASCENDING
                    ? SortDirections.DESCENDING
                    : SortDirections.ASCENDING,
        }));
    };

    const handleSearch = term => {
        setSearchTerm(term);
    };

    const renderSortArrow = key => {
        if (sortConfig.key !== key) {
            return null;
        }
        return (
            <span className="px-2">
                <svg
                    width="11"
                    height="6"
                    viewBox="0 0 11 6"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d={
                            sortConfig.direction === SortDirections.ASCENDING
                                ? 'M1 5.25L5.25 0.75L9.5 5.25'
                                : 'M1 0.75L5.25 5.25L9.5 0.75'
                        }
                        stroke="#2F2B43"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </span>
        );
    };

    // TODO: remove these and use the service
    const downloadOriginalFile = (id, tenantId, fileName) => {
        const anchor = document.createElement('a');
        document.body.appendChild(anchor);

        getOriginalFile(id, tenantId).then(blobby => {
            const objectUrl = window.URL.createObjectURL(blobby);
            anchor.href = objectUrl;
            anchor.download = encodeURIComponent(fileName);
            anchor.click();
            window.URL.revokeObjectURL(objectUrl);
        });

        document.body.removeChild(anchor);
    };
    // TODO: remove these and use the service
    const downloadReport = (id, tenantId, type) => {
        const anchor = document.createElement('a');
        document.body.appendChild(anchor);

        getImportReport(id, tenantId).then(blobby => {
            const objectUrl = window.URL.createObjectURL(blobby);
            anchor.href = objectUrl;
            anchor.download = createReportFileName(id, type);
            anchor.click();
            window.URL.revokeObjectURL(objectUrl);
            logEvent(
                LogEventType.DataDownloaded,
                `Imports queue report downloaded`,
            );
        });

        document.body.removeChild(anchor);
    };
    // TODO: remove these and use the service
    const createReportFileName = (id, type) => {
        return `import ${encodeURIComponent(id)} ${encodeURIComponent(type)} report.csv`;
    };

    const sortedImports = useMemo(() => {
        return [...filteredData].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (!aValue) aValue = '';
            if (!bValue) bValue = '';

            if (sortConfig.key === 'dateTime') {
                // Convert to Date objects for comparison
                const dateA = new Date(aValue);
                const dateB = new Date(bValue);

                if (dateA < dateB) {
                    return sortConfig.direction === SortDirections.ASCENDING
                        ? -1
                        : 1;
                }
                if (dateA > dateB) {
                    return sortConfig.direction === SortDirections.ASCENDING
                        ? 1
                        : -1;
                }
                return 0;
            }

            // For string fields, compare case-insensitive
            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();

            if (aValue < bValue) {
                return sortConfig.direction === SortDirections.ASCENDING
                    ? -1
                    : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === SortDirections.ASCENDING
                    ? 1
                    : -1;
            }
            return 0;
        });
    }, [filteredData, sortConfig]);

    const handleCheckboxChange = importId => {
        if (checkedImportIds.indexOf(importId) !== -1) {
            var newArray = checkedImportIds.filter(item => item !== importId);
            setCheckedImportIds(newArray);
        } else {
            var newArray = [...checkedImportIds, importId];
            setCheckedImportIds(newArray);
        }
    };

    const handleDeleteClick = async () => {
        if (!checkedImportIds.length) {
            Toast.error(getText('msg_9bd1b9956da8'));
            return;
        }

        setIsLoading(true);

        try {
            const selectedImports = imports.filter(importItem =>
                checkedImportIds.includes(importItem.id),
            );

            const deleteTargets = Array.from(
                selectedImports
                    .reduce((map, importItem) => {
                        const batchKey = importItem.batchId ?? importItem.id;

                        if (!map.has(batchKey)) {
                            map.set(batchKey, importItem);
                        }

                        return map;
                    }, new Map())
                    .values(),
            );

            const results = await Promise.all(
                deleteTargets.map(async importItem => {
                    const response = await deleteImport(importItem.id);
                    return { importItem, response };
                }),
            );

            const successfulBatchIds = results
                .filter(({ response }) => response?.status === 200)
                .map(({ importItem }) => importItem.batchId ?? importItem.id);

            const failedDeletes = results.filter(
                ({ response }) => response?.status !== 200,
            );

            if (successfulBatchIds.length > 0) {
                const remainingImports = imports.filter(
                    importItem =>
                        !successfulBatchIds.includes(
                            importItem.batchId ?? importItem.id,
                        ),
                );

                setImports(remainingImports);
                setBaseFilteredData(remainingImports);

                Toast.success(
                    successfulBatchIds.length === 1
                        ? getText('msg_4eb1c7a2d901')
                        : getText('msg_5fc2d8b3ea12', {
                              count: successfulBatchIds.length,
                          }),
                );
            }

            failedDeletes.forEach(({ response, importItem }) => {
                if (response?.error) {
                    Toast.error(response.message);
                } else {
                    Toast.error(
                        getText('msg_6ad3e9c4fb23', {
                            importId: importItem.id,
                        }),
                    );
                }
            });

            setCheckedImportIds([]);
        } catch (error) {
            console.error('Failed to fetch or delete import data', error);
            Toast.error(getText('msg_7be4fad50c34'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="page-content one-hundred-percent-height">
            <div className="card one-hundred-percent-min-height">
                <div className="card-body px-0 py-4">
                    {/* Page Header with Title and Actions */}
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div className="float-start d-flex align-items-center">
                            <p className="page-title card-title ms-4 me-5 mb-0">
                                {getText('msg_201bb20e8ef2')}
                            </p>
                        </div>
                        <div className="float-end d-flex align-items-center">
                            <span
                                type="button"
                                onClick={handleDeleteClick}
                                className="ing-text-buttons mx-3 me-4 pe-4"
                            >
                                <i className="bi bi-trash3 me-1" />
                                {getText('msg_f6fdbe48dc54')}
                            </span>
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="text-center">
                            <Spinner className={styles.loadingSpinner} />
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive-xxl w-100">
                                <table className="table table-hover align-middle ing-table-striped">
                                    <thead className="custom-table-header table-light">
                                        <tr>
                                            <th></th>
                                            <th
                                                className={styles.columnHead}
                                                onClick={() => handleSort('id')}
                                            >
                                                <span className="table-column-header-text">
                                                    {getText(
                                                        'msg_89f89c02cf47',
                                                    )}
                                                    {renderSortArrow('id')}
                                                </span>
                                            </th>
                                            <th
                                                className={styles.columnHead}
                                                onClick={() =>
                                                    handleSort('dateTime')
                                                }
                                            >
                                                <span className="table-column-header-text">
                                                    {getText(
                                                        'msg_60a57de73d21',
                                                    )}{' '}
                                                    {renderSortArrow(
                                                        'dateTime',
                                                    )}
                                                </span>
                                            </th>
                                            <th className={styles.columnHead}>
                                                <span className="d-flex align-items-center">
                                                    <span
                                                        className="table-column-header-text"
                                                        onClick={() =>
                                                            handleSort(
                                                                'template',
                                                            )
                                                        }
                                                    >
                                                        {getText(
                                                            'msg_709a23220f2c',
                                                        )}{' '}
                                                        {renderSortArrow(
                                                            'template',
                                                        )}
                                                    </span>
                                                    <span
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            openModal(
                                                                'template',
                                                            );
                                                        }}
                                                        style={{
                                                            cursor: 'pointer',
                                                            marginLeft: '5px',
                                                        }}
                                                    >
                                                        {renderFilterIcon(
                                                            selections[
                                                                'template'
                                                            ]?.length > 0,
                                                        )}
                                                    </span>
                                                    {activeModal ===
                                                        'template' && (
                                                        <FilterModal
                                                            column={activeModal}
                                                            options={getOptions(
                                                                activeModal,
                                                            )}
                                                            selectedOptions={
                                                                selections[
                                                                    activeModal
                                                                ] || []
                                                            }
                                                            onApply={
                                                                handleApply
                                                            }
                                                            onClose={closeModal}
                                                        />
                                                    )}
                                                </span>
                                            </th>
                                            <th className={styles.columnHead}>
                                                <span className="d-flex align-items-center">
                                                    <span
                                                        className="table-column-header-text"
                                                        onClick={() =>
                                                            handleSort(
                                                                'importType',
                                                            )
                                                        }
                                                    >
                                                        {getText(
                                                            'msg_78cfb1f529c1',
                                                        )}{' '}
                                                        {renderSortArrow(
                                                            'importType',
                                                        )}
                                                    </span>
                                                    <span
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            openModal(
                                                                'importType',
                                                            );
                                                        }}
                                                        style={{
                                                            cursor: 'pointer',
                                                            marginLeft: '5px',
                                                        }}
                                                    >
                                                        {renderFilterIcon(
                                                            selections[
                                                                'importType'
                                                            ]?.length > 0,
                                                        )}
                                                    </span>
                                                    {activeModal ===
                                                        'importType' && (
                                                        <FilterModal
                                                            column={activeModal}
                                                            options={getOptions(
                                                                activeModal,
                                                            )}
                                                            selectedOptions={
                                                                selections[
                                                                    activeModal
                                                                ] || []
                                                            }
                                                            onApply={
                                                                handleApply
                                                            }
                                                            onClose={closeModal}
                                                        />
                                                    )}
                                                </span>
                                            </th>
                                            <th className={styles.columnHead}>
                                                <span className="d-flex align-items-center">
                                                    <span
                                                        className="table-column-header-text"
                                                        onClick={() =>
                                                            handleSort(
                                                                'fileType',
                                                            )
                                                        }
                                                    >
                                                        {getText(
                                                            'msg_12c8f2c6b412',
                                                        )}{' '}
                                                        {renderSortArrow(
                                                            'fileType',
                                                        )}
                                                    </span>
                                                    <span
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            openModal(
                                                                'fileType',
                                                            );
                                                        }}
                                                        style={{
                                                            cursor: 'pointer',
                                                            marginLeft: '5px',
                                                        }}
                                                    >
                                                        {renderFilterIcon(
                                                            selections[
                                                                'fileType'
                                                            ]?.length > 0,
                                                        )}
                                                    </span>
                                                    {activeModal ===
                                                        'fileType' && (
                                                        <FilterModal
                                                            column={activeModal}
                                                            options={getOptions(
                                                                activeModal,
                                                            )}
                                                            selectedOptions={
                                                                selections[
                                                                    activeModal
                                                                ] || []
                                                            }
                                                            onApply={
                                                                handleApply
                                                            }
                                                            onClose={closeModal}
                                                        />
                                                    )}
                                                </span>
                                            </th>
                                            <th className={styles.columnHead}>
                                                <span className="d-flex align-items-center">
                                                    <span
                                                        className="table-column-header-text"
                                                        onClick={() =>
                                                            handleSort('status')
                                                        }
                                                    >
                                                        {getText(
                                                            'msg_bae7d5be7082',
                                                        )}{' '}
                                                        {renderSortArrow(
                                                            'status',
                                                        )}
                                                    </span>
                                                    <span
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            openModal('status');
                                                        }}
                                                        style={{
                                                            cursor: 'pointer',
                                                            marginLeft: '5px',
                                                        }}
                                                    >
                                                        {renderFilterIcon(
                                                            selections['status']
                                                                ?.length > 0,
                                                        )}
                                                    </span>
                                                    {activeModal ===
                                                        'status' && (
                                                        <FilterModal
                                                            column={activeModal}
                                                            options={getOptions(
                                                                activeModal,
                                                            )}
                                                            selectedOptions={
                                                                selections[
                                                                    activeModal
                                                                ] || []
                                                            }
                                                            onApply={
                                                                handleApply
                                                            }
                                                            onClose={closeModal}
                                                        />
                                                    )}
                                                </span>
                                            </th>
                                            <th
                                                className={`text-center ${styles.columnHead}`}
                                                onClick={() =>
                                                    handleSort('test')
                                                }
                                            >
                                                <span className="table-column-header-text">
                                                    {getText(
                                                        'msg_640ab2bae07b',
                                                    )}{' '}
                                                    {renderSortArrow('test')}
                                                </span>
                                            </th>
                                            <th
                                                className={styles.columnHead}
                                                onClick={() =>
                                                    handleSort('totalRecords')
                                                }
                                            >
                                                <span className="table-column-header-text">
                                                    {getText(
                                                        'msg_e51c55255be9',
                                                    )}{' '}
                                                    {renderSortArrow(
                                                        'totalRecords',
                                                    )}
                                                </span>
                                            </th>
                                            <th className={styles.columnHead}>
                                                <span className="d-flex align-items-center">
                                                    <span
                                                        className="table-column-header-text"
                                                        onClick={() =>
                                                            handleSort(
                                                                'submittedBy',
                                                            )
                                                        }
                                                    >
                                                        {getText(
                                                            'msg_6f6684287ce1',
                                                        )}{' '}
                                                        {renderSortArrow(
                                                            'submittedBy',
                                                        )}
                                                    </span>
                                                    <span
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            openModal(
                                                                'submittedBy',
                                                            );
                                                        }}
                                                        style={{
                                                            cursor: 'pointer',
                                                            marginLeft: '5px',
                                                        }}
                                                    >
                                                        {renderFilterIcon(
                                                            selections[
                                                                'submittedBy'
                                                            ]?.length > 0,
                                                        )}
                                                    </span>
                                                    {activeModal ===
                                                        'submittedBy' && (
                                                        <FilterModal
                                                            column={activeModal}
                                                            options={getOptions(
                                                                activeModal,
                                                            )}
                                                            selectedOptions={
                                                                selections[
                                                                    activeModal
                                                                ] || []
                                                            }
                                                            onApply={
                                                                handleApply
                                                            }
                                                            onClose={closeModal}
                                                        />
                                                    )}
                                                </span>
                                            </th>
                                            <th className="text-center">
                                                <span className="table-column-header-text">
                                                    {getText(
                                                        'msg_9d4028d5c539',
                                                    )}
                                                </span>
                                            </th>
                                            <th className="text-center">
                                                <span className="table-column-header-text">
                                                    {getText(
                                                        'msg_edf6f88b77a8',
                                                    )}
                                                </span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedImports.length === 0 ? (
                                            <tr>
                                                <td
                                                    colSpan={12}
                                                    className="text-center"
                                                >
                                                    <NotFoundBox
                                                        backgroundColor="#f0f4ff"
                                                        title={getText(
                                                            'msg_42649d11c41c',
                                                        )}
                                                        description={getText(
                                                            'msg_bff1ed5685d3',
                                                        )}
                                                    />
                                                </td>
                                            </tr>
                                        ) : (
                                            sortedImports.map(importRecord => (
                                                <tr key={importRecord.id}>
                                                    <td>
                                                        {importRecord.canDelete && (
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={
                                                                    (
                                                                        checkedImportIds ??
                                                                        []
                                                                    ).length &&
                                                                    checkedImportIds.indexOf(
                                                                        importRecord.id,
                                                                    ) != -1
                                                                }
                                                                onChange={() =>
                                                                    handleCheckboxChange(
                                                                        importRecord.id,
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                    </td>
                                                    <td className="item-page-last-edited">
                                                        {importRecord.id}
                                                    </td>
                                                    <td className="item-page-last-edited">
                                                        {importRecord.dateTime}
                                                    </td>
                                                    <td className="item-page-last-edited">
                                                        {importRecord.template}
                                                    </td>
                                                    <td className="item-page-last-edited">
                                                        {
                                                            importRecord.importType
                                                        }
                                                    </td>
                                                    <td className="item-page-last-edited">
                                                        {importRecord.fileType}
                                                    </td>
                                                    <td>
                                                        <span
                                                            className={`item-page-status badge-base ${getBadgeStatus().Class(
                                                                importRecord.status,
                                                            )}`}
                                                        >
                                                            <svg
                                                                className="me-1"
                                                                width={8}
                                                                height={8}
                                                                viewBox="0 0 8 8"
                                                                fill="none"
                                                            >
                                                                <circle
                                                                    cx={4}
                                                                    cy={4}
                                                                    r={3}
                                                                    fill={getBadgeStatus().SVGColor(
                                                                        importRecord.status,
                                                                    )}
                                                                />
                                                            </svg>
                                                            {
                                                                importRecord.status
                                                            }
                                                        </span>
                                                    </td>
                                                    <td className="text-center">
                                                        {importRecord.test
                                                            ? 'Y'
                                                            : 'N'}
                                                    </td>
                                                    <td className="item-page-changed-by">
                                                        {importRecord.records}
                                                    </td>
                                                    <td className="item-page-changed-by">
                                                        {
                                                            importRecord.submittedBy
                                                        }
                                                    </td>
                                                    <td className="item-page-changed-by text-center">
                                                        {importRecord.resultsReportId >
                                                            0 && (
                                                            <span
                                                                type="button"
                                                                className="button-base ing-button-primary-left"
                                                                onClick={() =>
                                                                    downloadReport(
                                                                        importRecord.resultsReportId,
                                                                        importRecord.tenantId,
                                                                        'results',
                                                                    )
                                                                }
                                                            >
                                                                {getText(
                                                                    'msg_a479c9c34e87',
                                                                )}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="item-page-changed-by text-center">
                                                        <span
                                                            type="button"
                                                            className="button-base ing-button-primary-left"
                                                            onClick={() =>
                                                                downloadOriginalFile(
                                                                    importRecord.id,
                                                                    importRecord.tenantId,
                                                                    importRecord.displayFileName,
                                                                )
                                                            }
                                                        >
                                                            {getText(
                                                                'msg_a479c9c34e87',
                                                            )}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {sortedImports.length > 0 && (
                                <PaginationComponent
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImportQueuePage;
